import {
    LayoutDashboard,
    TrendingUp,
    Users2,
    FolderKanban,
    Wallet,
    FileText,
    MessageSquare,
    ShieldCheck,
    Zap,
    Globe2,
    Clock,
    Headset,
    BarChart3,
    Sparkles,
} from "lucide-react";

export const DEPARTMENTS = [
    "Sales",
    "HR",
    "Operations",
    "Accounts & Finance",
    "Project Management",
    "Communication",
];

export const MODULES = [
    {
        id: "dashboard",
        name: "Dashboard",
        department: "Operations",
        icon: LayoutDashboard,
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
        features: [
            "Executive overview",
            "Outcomes",
            "Module KPI Dashboards",
            "Charts & Analytics",
            "Leadership briefings",
            "Summary Reports",
        ],
    },
    {
        id: "sales",
        name: "Sales",
        department: "Sales",
        icon: TrendingUp,
        accent: "text-cyan-300",
        accentBg: "bg-cyan-400/10 border-cyan-400/25",
        features: [
            "Leads",
            "Customer",
            "Quotations",
            "Bills",
            "Activities",
            "Sales Reports",
            "Bottleneck finder",
            "Sales Intelligence",
        ],
    },
    {
        id: "hr",
        name: "HR",
        department: "HR",
        icon: Users2,
        accent: "text-violet-300",
        accentBg: "bg-violet-400/10 border-violet-400/25",
        features: [
            "Employee",
            "Shifts",
            "Forms",
            "Onboarding",
            "Attendance",
            "Leaves",
            "Payroll",
            "Interns",
            "Tasks",
        ],
    },
    {
        id: "projects",
        name: "Projects",
        department: "Project Management",
        icon: FolderKanban,
        accent: "text-emerald-300",
        accentBg: "bg-emerald-400/10 border-emerald-400/25",
        features: [
            "All Projects",
            "Teams",
            "Tasks",
            "Calendar",
            "Scheduling",
            "Timesheet",
            "Gantt",
            "Workload Management",
            "Wiki",
            "Knowledgebase",
            "Whiteboards",
        ],
    },
    {
        id: "finance",
        name: "Finance",
        department: "Accounts & Finance",
        icon: Wallet,
        accent: "text-amber-300",
        accentBg: "bg-amber-400/10 border-amber-400/25",
        features: [
            "Sales",
            "Purchase",
            "Transactions",
            "Ledgers",
            "Cashflow",
            "P&L",
            "Forecast",
            "Balance sheets",
            "Chart of accounts",
            "Invoices",
        ],
    },
    {
        id: "documents",
        name: "Documents",
        department: "Operations",
        icon: FileText,
        accent: "text-sky-300",
        accentBg: "bg-sky-400/10 border-sky-400/25",
        features: [
            "File storage & folders",
            "Shared workspaces",
            "Document Categories",
            "Document Tags",
            "Document Search",
            "Document Sharing",
            "Document Folders",
        ],
    },
    {
        id: "communications",
        name: "Communications",
        department: "Communication",
        icon: MessageSquare,
        accent: "text-rose-300",
        accentBg: "bg-rose-400/10 border-rose-400/25",
        features: [
            "Team channels",
            "Direct messages",
            "Announcements",
        ],
    },
    {
        id: "analytics",
        name: "Analytics",
        department: "Operations",
        icon: BarChart3,
        accent: "text-indigo-300",
        accentBg: "bg-indigo-400/10 border-indigo-400/25",
        features: [
            "Reporting",
            "Dashboards",
            "Sales Intelligence",
            "Cross-module KPIs",
            "Custom exports",
        ],
    },
    {
        id: "ai",
        name: "AI & Automation",
        department: "Operations",
        icon: Sparkles,
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/30",
        features: [
            "AI Co-pilot",
            "Cross-module AI Intelligence",
            "AI-drafts with citations",
            "Forecasting & Insights",
        ],
    },
];

const SHARED_HIGHLIGHTS = [
    "All 9 modules · full feature set",
    "Dashboard, Sales, HR, Projects, Finance & more",
    "AI Co‑pilot & cross-module intelligence",
    "99.99% SLA",
    "SOC 2 & ISO 27001 compliant",
    "Priority support",
    "Dedicated CSM",
    "SAML SSO",
    "Custom storage",
];

export const PRICING_CURRENCY = "INR";

export const TIERS = [
    {
        id: "starter",
        name: "Starter",
        audience: "Small teams building their operating foundation.",
        licenses: "10 – 50 licenses",
        monthly: 1999,
        yearly: 19100,
        cta: "Purchase Now",
        primary: false,
        highlights: SHARED_HIGHLIGHTS,
    },
    {
        id: "business",
        name: "Business",
        audience: "Growing companies running every function at scale.",
        licenses: "50 – 350 licenses",
        monthly: 1499,
        yearly: 14300,
        cta: "Purchase Now",
        primary: true,
        highlights: SHARED_HIGHLIGHTS,
    },
    {
        id: "enterprise",
        name: "Enterprise",
        audience: "Large organisations with dedicated scale & support.",
        licenses: "350+ licenses",
        monthly: null,
        yearly: null,
        cta: "Contact Sales",
        primary: false,
        highlights: SHARED_HIGHLIGHTS,
    },
];

export const TIER_LICENSE_BOUNDS = {
    starter: { min: 10, max: 50 },
    business: { min: 50, max: 350 },
    enterprise: { min: 350, max: null },
};

export const getTierById = (tierId) =>
    TIERS.find((t) => t.id === tierId) ?? TIERS[0];

export const getTierLicenseBounds = (tierId) =>
    TIER_LICENSE_BOUNDS[tierId] ?? TIER_LICENSE_BOUNDS.starter;

export const clampLicenseCount = (tierId, count) => {
    const bounds = getTierLicenseBounds(tierId);
    const n = Number(count);
    if (!Number.isFinite(n)) return bounds.min;
    const max = bounds.max ?? n;
    return Math.min(Math.max(Math.round(n), bounds.min), max);
};

/** Per-license display amounts for pricing tables (yearly shows list vs offer). */
export const getTierDisplayPricing = (tier, yearly) => {
    if (!tier || tier.monthly === null) return null;

    const listMonthly = tier.monthly;
    const offerMonthly = yearly ? Math.round(tier.yearly / 12) : tier.monthly;
    const listYearly = tier.monthly * 12;
    const offerYearly = tier.yearly;
    const savingsPercent = yearly
        ? Math.round((1 - tier.yearly / listYearly) * 100)
        : 0;

    return {
        listMonthly,
        offerMonthly,
        listYearly,
        offerYearly,
        savingsPercent,
        showOffer: yearly && offerMonthly < listMonthly,
    };
};

/** @returns {{ licenseCount, perLicense, amount, billingPeriod, label, currency }} | null */
export const calculatePricingTotal = (tierId, licenseCount, yearly) => {
    const tier = getTierById(tierId);
    if (!tier || tier.monthly === null) return null;

    const licenseCountClamped = clampLicenseCount(tierId, licenseCount);
    const perLicense = yearly ? tier.yearly : tier.monthly;
    const amount = licenseCountClamped * perLicense;

    return {
        licenseCount: licenseCountClamped,
        perLicense,
        perLicenseMonthlyDisplay: yearly
            ? Math.round(tier.yearly / 12)
            : tier.monthly,
        amount,
        currency: PRICING_CURRENCY,
        billingPeriod: yearly ? "year" : "month",
        label: yearly ? "Annual" : "Monthly",
    };
};

export const formatUsd = (amount) => formatMoney(amount, PRICING_CURRENCY);

export const formatPrice = (amount) => formatMoney(amount, PRICING_CURRENCY);

export const formatMoney = (amount, currency = PRICING_CURRENCY) =>
    new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);

export const PLATFORM_COMPARE = [
    {
        title: "Plans at a glance",
        rows: [
            { f: "License range", v: ["10 – 50", "50 – 350", "350+"] },
            { f: "All module features", v: ["true", "true", "true"] },
            { f: "Storage / license", v: ["25 GB", "500 GB", "Custom"] },
            { f: "SAML SSO", v: ["true", "true", "true"] },
            { f: "Human support", v: ["Real engineers in our docs - not outsourced ticket farms.", "Real engineers in our docs - not outsourced ticket farms.", "Real engineers in our docs - not outsourced ticket farms."] },
            { f: "Priority support", v: ["true", "true", "true"] },
            { f: "Dedicated CSM", v: ["false", "true", "true"] },
            { f: "99.99% SLA", v: ["false", "false", "true"] },
        ],
    },
];

export const EVERY_PLAN = [
    // {
    //     icon: ShieldCheck,
    //     t: "Secure by default",
    //     d: "GDPR compliant. Encrypted at rest & in transit.",
    // },
    // {
    //     icon: Zap,
    //     t: "Real‑time sync",
    //     d: "Every event propagates instantly across all modules and departments.",
    // },
    // {
    //     icon: Globe2,
    //     t: "Global edge",
    //     d: "14 PoPs across 8 regions. p95 latency under 26ms.",
    // },
    {
        icon: Clock,
        t: "Version history",
        d: "Full event and document history retained for audit and rollback.",
    },
    {
        icon: Users2,
        t: "Department alignment",
        d: "Sales, HR, Operations, Finance, Projects & Communication in one graph.",
    },
    {
        icon: Headset,
        t: "Human support",
        d: "Real engineers in our docs — not outsourced ticket farms.",
    },
];

export const PRICING_FAQS = [
    {
        q: "What counts as a license?",
        a: "A license is one active user with full access to every ApexBrain module and feature. Guest and read-only seats are unlimited on every plan.",
    },
    {
        q: "Do all plans include the same features?",
        a: "Yes. Starter, Business and Enterprise all include the full feature set across Dashboard, Sales, HR, Projects, Finance, Documents, Communications, Analytics and AI & Automation. Plans differ only by license count and pricing.",
    },
    {
        q: "When should I move from Starter to Business?",
        a: "Starter covers 10 – 50 licenses. When your team grows past 50, upgrade to Business (50 – 350 licenses). Your data, workflows and history carry over instantly.",
    },
    {
        q: "What happens above 350 licenses?",
        a: "Teams with more than 350 licenses move to Enterprise. You get the same full feature set plus dedicated account management, custom storage and a 99.99% SLA.",
    },
    {
        q: "Is there a free trial?",
        a: "Business includes a 14‑day free trial with full access. No credit card required.",
    },
    {
        q: "Do you offer annual billing?",
        a: "Yes. Annual billing saves 20% on Starter and Business. Enterprise is billed annually by contract.",
    },
];
